import config from '@/../service/project.config.js';
import router from '@/router';
import { useAppStore } from '@/stores/app.store';
import { useNotificationStore } from '@/stores/notification.store';
import {
  NotificationDisplayDuration,
  NotificationDisplayTiming,
  NotificationType,
} from '@/types/notificationTypes';
import axios, { AxiosError, AxiosResponse } from 'axios';

const UNKNOWN_ERROR_KEY = 'Error.Unknown';
const UNKNOWN_ERROR_FALLBACK_MESSAGE = 'An unexpected error occurred. Please try again later.';

// set authentication
axios.defaults.withCredentials = process.env.NODE_ENV === 'production' ? true : false;

// disable redirects from api
axios.defaults.maxRedirects = 0;

// catch error by interceptors
const getUnknownErrorMessage = (): string => {
  try {
    const appStore = useAppStore();

    if (appStore?.isTranslationsFetched) {
      const translated = appStore.translations?.[UNKNOWN_ERROR_KEY];

      if (translated && typeof translated === 'string' && translated.trim()) {
        return translated;
      }
    }
  } catch {
    // ignore and fallback below
  }

  return UNKNOWN_ERROR_FALLBACK_MESSAGE;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const notifyUnknownError = (): void => {
  try {
    const notificationStore = useNotificationStore();
    notificationStore.addNotification({
      message: getUnknownErrorMessage(),
      type: NotificationType.Error,
      displayTiming: NotificationDisplayTiming.Immediately,
      displayDuration: NotificationDisplayDuration.Once,
    });
  } catch (e) {
    // If the store can't be used yet (Pinia not initialized), avoid crashing.
    console.error('Failed to show unknown error notification', e);
  }
};

axios.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error: AxiosError) => {
    if (error?.response && [301, 302].includes(error?.response?.status)) {
      console.info('AXIOS CATCH REDIRECT: ', error?.response?.status);
    } else if (error?.response?.status === 401 || error?.response?.status === 403) {
      window.location.href = '/error/accessdenied';
      console.info('AXIOS CATCH ERROR: ', error?.response?.status);
    } else if (error?.response?.status === 400) {
      return error.response;
    } else if (error?.response?.status === 500) {
      return error.response;
    } else if (error?.response?.status === 404) {
      router.push({ path: '/notFound' });
    } else if (axios.isCancel(error)) {
      console.info('AXIOS REQUEST CANCELLED', error.message);
    } else {
      console.error('AXIOS CATCH UNKNOWN ERROR: ', error);
    }
    return Promise.reject(error);
  },
);

axios.interceptors.request.use(
  async (requestConfig) => {
    if (
      !requestConfig.headers?.isDisableAntiforgeryToken &&
      requestConfig.method &&
      ['post', 'put', 'patch', 'delete'].includes(requestConfig.method?.toLowerCase())
    ) {
      try {
        await axios.get(`${config.backend}/antiforgery/token`);
        const csrfToken = document.cookie
          .split('; ')
          .find((row) => row.startsWith('anti-csrf-token='))
          ?.split('=')[1];

        if (csrfToken) {
          requestConfig.headers['anti-csrf-token'] = csrfToken;
        }
      } catch (error) {
        console.error('Error fetching CSRF token: ', error);
        return Promise.reject(error);
      }
    }
    return requestConfig;
  },
  (error) => {
    return Promise.reject(error);
  },
);
